import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import Card, { EmptyBlock, LegendKey } from "./Card";
import ChartTooltip from "./ChartTooltip";
import { COLORS, FONT, MUTED_MARK, SERIES, niceTicks } from "./theme";

/**
 * Gráficos do dashboard.
 *
 * Convenções aplicadas em todos (e o motivo de cada uma):
 * - Grade em hairline SÓLIDA e só no eixo do valor. Tracejado lê como
 *   "projeção" ou "limite" quando é só grade; grade vertical em série temporal
 *   só some com a leitura.
 * - Barra no máximo 24px, com a ponta do dado arredondada em 4px e a base
 *   quadrada — a barra cresce de uma linha de base, ela não flutua.
 * - Eixos e rótulos em tinta apagada; o dado é a única coisa que pode ser
 *   forte na tela.
 * - Nenhum gráfico tem dois eixos Y. Onde há duas medidas, elas dividem a mesma
 *   escala de contagem (pessoas ≤ preenchimentos, sempre).
 */

const axisTick = {
  fontSize: 11,
  fill: COLORS.inkMuted,
  fontFamily: FONT,
  // Nas colunas de um eixo os números precisam alinhar: aqui `tabular-nums` é
  // exatamente o caso de uso.
  style: { fontVariantNumeric: "tabular-nums" },
};

const MAX_BAR = 24;

/**
 * Série temporal: volume e pessoas distintas, empilhados como painéis irmãos.
 *
 * Por que DOIS painéis e não uma linha por cima das colunas: as duas medidas
 * vivem em escalas muito diferentes (dezenas de preenchimentos contra uma
 * dúzia de pessoas), então a linha ficaria colada na base e não se leria. Dar
 * a ela um segundo eixo Y no mesmo gráfico é pior ainda — o alinhamento entre
 * duas escalas é arbitrário e inventa uma correlação que não está no dado.
 * Painéis separados, com o MESMO eixo X alinhado, comparam a forma das duas
 * curvas sem mentir sobre a proporção.
 */
export function SeriesChart({ series, granularityLabel }) {
  const hasData = series.some((point) => point.total > 0);

  // Os dois painéis precisam do mesmo recuo à esquerda e da mesma largura de
  // eixo, senão o dia N de cima não fica sobre o dia N de baixo.
  const margin = { top: 6, right: 10, bottom: 0, left: 0 };
  const axisWidth = 46;
  const totalAxis = niceTicks(series.map((point) => point.total));
  const usersAxis = niceTicks(series.map((point) => point.users), 2);

  return (
    <Card
      title={`Preenchimentos por ${granularityLabel}`}
      hint="O painel de baixo mostra quantas pessoas diferentes preencheram — dois picos iguais com metade da equipe contam outra história"
    >
      {!hasData ? (
        <EmptyBlock height={240}>Nenhum preenchimento no período selecionado.</EmptyBlock>
      ) : (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 2 }}>
            <LegendKey color={SERIES.fills} label="Preenchimentos" />
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={series} margin={margin}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              {/* Eixo X só no painel de baixo: repetir as datas duas vezes é
                  ruído, e o alinhamento vertical já liga os dois. */}
              <XAxis dataKey="label" hide />
              <YAxis
                allowDecimals={false}
                tick={axisTick}
                tickLine={false}
                axisLine={false}
                width={axisWidth}
                domain={[0, totalAxis.max]}
                ticks={totalAxis.ticks}
              />
              <RechartsTooltip
                cursor={{ fill: "rgba(31, 138, 91, 0.07)" }}
                content={<ChartTooltip />}
              />
              <Bar
                dataKey="total"
                name="Preenchimentos"
                fill={SERIES.fills}
                maxBarSize={MAX_BAR}
                radius={[4, 4, 0, 0]}
              />
            </ComposedChart>
          </ResponsiveContainer>

          <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0 2px" }}>
            <LegendKey color={SERIES.people} label="Pessoas distintas" />
          </div>
          <ResponsiveContainer width="100%" height={104}>
            <ComposedChart data={series} margin={margin}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis
                dataKey="label"
                tick={axisTick}
                tickLine={false}
                axisLine={{ stroke: COLORS.axis }}
                interval="preserveStartEnd"
                minTickGap={18}
              />
              <YAxis
                allowDecimals={false}
                tick={axisTick}
                tickLine={false}
                axisLine={false}
                width={axisWidth}
                // Duas divisões só: o painel tem 100px de altura e uma dúzia de
                // pessoas — cinco marcas aqui viram ruído.
                domain={[0, usersAxis.max]}
                ticks={usersAxis.ticks}
              />
              <RechartsTooltip
                cursor={{ stroke: COLORS.axis }}
                content={<ChartTooltip />}
              />
              <Line
                type="monotone"
                dataKey="users"
                name="Pessoas"
                stroke={SERIES.people}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: COLORS.surface }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

/**
 * Ranking top-N — barras horizontais em HTML, não em recharts.
 *
 * Um eixo de categoria com nome de formulário é uma briga perdida no recharts:
 * ou ele quebra "Limpeza de Instalações e Equipamentos" em duas linhas e
 * desalinha as faixas, ou o corte fica tão curto que o nome perde o sentido.
 * Em HTML o nome ganha uma linha inteira com reticências de CSS (e o nome
 * completo no `title`), e o valor fica SEMPRE visível ao lado — sem depender
 * de hover, que é exatamente o alívio que a checagem de contraste pede.
 *
 * Continua sendo um gráfico de barras: mesma linha de base, mesmo comprimento
 * proporcional ao valor, mesma cor de série.
 */
export function TopChart({ title, hint, rows, labelKey, topN = 8, emptyText }) {
  const data = rows.slice(0, topN);
  const max = data.reduce((acc, row) => Math.max(acc, row.total), 0) || 1;

  return (
    <Card title={title} hint={hint} flex="1 1 420px" minWidth={320}>
      {data.length === 0 ? (
        <EmptyBlock height={180}>{emptyText}</EmptyBlock>
      ) : (
        <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {data.map((row) => (
            <li key={row[labelKey]} style={{ marginBottom: 13 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 12,
                  marginBottom: 5,
                }}
              >
                <span
                  title={row[labelKey]}
                  style={{
                    fontFamily: FONT,
                    fontSize: 12.5,
                    color: COLORS.inkSecondary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {row[labelKey]}
                </span>
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: COLORS.ink,
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                  }}
                >
                  {row.total.toLocaleString("pt-BR")}
                </span>
              </div>
              <div
                style={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: COLORS.subtle,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    // Proporcional ao maior valor da lista: a barra do topo
                    // preenche a faixa e as demais se comparam com ela.
                    width: `${(row.total / max) * 100}%`,
                    backgroundColor: SERIES.fills,
                    borderRadius: 999,
                  }}
                />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

/** Ritmo: dia da semana e hora do dia, lado a lado no mesmo card. */
export function RhythmChart({ byWeekday, byHour }) {
  const hasData = byWeekday.some((day) => day.total > 0);
  const weekdayAxis = niceTicks(byWeekday.map((day) => day.total));
  const hourAxis = niceTicks(byHour.map((hour) => hour.total));

  return (
    <Card
      title="Ritmo de preenchimento"
      hint="Quando os registros acontecem de fato — útil para bater com a escala de turno"
      action={
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <LegendKey color={SERIES.fills} label="Dia útil" />
          <LegendKey color={MUTED_MARK} label="Fim de semana" />
        </div>
      }
    >
      {!hasData ? (
        <EmptyBlock height={180}>Nenhum preenchimento no período selecionado.</EmptyBlock>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
          <div style={{ flex: "1 1 260px", minWidth: 240 }}>
            <ResponsiveContainer width="100%" height={180}>
              {/* left: 0 e width do eixo generosa — com recuo negativo, um
                  valor de 4 dígitos sai cortado pela borda do card. */}
              <BarChart data={byWeekday} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={COLORS.grid} vertical={false} />
                <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={{ stroke: COLORS.axis }} />
                <YAxis
                  allowDecimals={false}
                  tick={axisTick}
                  tickLine={false}
                  axisLine={false}
                  width={46}
                  domain={[0, weekdayAxis.max]}
                  ticks={weekdayAxis.ticks}
                />
                <RechartsTooltip cursor={{ fill: "rgba(31, 138, 91, 0.07)" }} content={<ChartTooltip />} />
                <Bar dataKey="total" name="Preenchimentos" maxBarSize={MAX_BAR} radius={[4, 4, 0, 0]}>
                  {/* Desênfase, não identidade: sábado e domingo continuam
                      sendo a MESMA série, só apagados, para o vale do fim de
                      semana não ser lido como queda de produtividade. */}
                  {byWeekday.map((day) => (
                    <Cell key={day.weekday} fill={day.weekday >= 5 ? MUTED_MARK : SERIES.fills} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p style={captionStyle}>Por dia da semana</p>
          </div>

          <div style={{ flex: "1 1 300px", minWidth: 260 }}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={byHour} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={COLORS.grid} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={axisTick}
                  tickLine={false}
                  axisLine={{ stroke: COLORS.axis }}
                  interval={2}
                />
                <YAxis
                  allowDecimals={false}
                  tick={axisTick}
                  tickLine={false}
                  axisLine={false}
                  width={46}
                  domain={[0, hourAxis.max]}
                  ticks={hourAxis.ticks}
                />
                <RechartsTooltip cursor={{ fill: "rgba(31, 138, 91, 0.07)" }} content={<ChartTooltip />} />
                <Bar
                  dataKey="total"
                  name="Preenchimentos"
                  fill={SERIES.fills}
                  maxBarSize={14}
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            <p style={captionStyle}>Por hora do dia</p>
          </div>
        </div>
      )}
    </Card>
  );
}

const captionStyle = {
  fontFamily: FONT,
  fontSize: 11.5,
  color: COLORS.inkMuted,
  textAlign: "center",
  margin: "6px 0 0",
};
